import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import type {
  BalanceListResponse,
  ClientDocument,
  ClientDocumentKind,
  ClientDocumentListResponse,
  LedgerListResponse,
  MovementFilters,
  PortfolioSummary,
  Statement,
  StatementFilters,
  StatementListResponse,
} from '../types/finance';

/** What a correction of a series' price per unit did for a client (S-52): money as strings with two
 *  decimals, units with six, a price per unit with eight. */
export interface NavCorrection {
  id: string;
  product_id: string;
  product_name: string;
  currency_code: string;
  as_of: string;
  superseded_nav_per_unit: string;
  corrected_nav_per_unit: string;
  units_issued: string;
  value_of_units_issued: string;
  cash_to_client: string;
  cash_status: 'payable' | 'owed_below_de_minimis' | null;
  payout_status: string | null;
  /** How owed cash is being paid: with the next payment, on request, or null while it is still owed. */
  owed_settled_with: 'next_payment' | 'on_request' | null;
  occurred_at: string;
}

export interface OwedPayout {
  correction_id: string;
  payout_id: string;
  created: boolean;
}

export interface ProductSummary {
  id: string;
  name: string;
  /** Name, asset class, currency, minimum, yield and term come live from the
   *  product's funds vehicle (0073) — the portal keeps no copy. All of them
   *  are null when funds could not answer for this vehicle; check
   *  `terms_available` rather than inferring that from any single field
   *  (a null `term_days` alone is a legitimate open-ended product). */
  currency_code: string | null;
  asset_class: string | null;
  description: string | null;
  min_investment: string | null;
  target_yield: string | null;
  term_days: number | null;
  risk_level: number | null;
  /** Redemption policy + early-redemption fee, straight from funds' /terms
   *  (audit #138 — funds is the ONLY source; the portal's commitment config
   *  never reaches client copy). Present only on the catalog / product-detail
   *  endpoints, which read funds per product; the portfolio listing omits
   *  them, and the UI falls back to "see product details" — never a guess. */
  lockup_days?: number | null;
  notice_period_days?: number | null;
  redemption_frequency?: string | null;
  early_redemption_method?: string | null;
  early_termination_retention_pct?: string | null;
  /** Annual management fee in basis points from funds' catalog (audit #140).
   *  A real 0 means "no management fee"; null means funds could not answer.
   *  Present only on the catalog / product-detail endpoints, like the
   *  redemption fields above. */
  management_fee_bps?: number | null;
  /** False when funds could not answer for this vehicle — every field
   *  above (except id/name/description/risk_level) is null in that case. */
  terms_available: boolean;
  /** Latest published product-scoped contract template version id, or
   *  null if no template exists for this product. Drives the "Invest
   *  in this product" CTA — hidden when null because there's nothing
   *  to sign yet. */
  contract_template_version_id?: string | null;
}

/**
 * Portfolio service. URL surface is `/portfolio/clients/{client_id}/*`
 * — no `/me` aliases. Callers (including the client themself) read
 * their own UUID from `authService.getCurrentUser()` (cached after
 * the first call) and pass it explicitly. Two convenience methods —
 * `getMyBalance`, `getMyMovements`, `listMyStatements`,
 * `generateMyStatement` — resolve the caller id internally so the
 * page code stays compact, but they all hit the same canonical URL.
 */
class PortfolioService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Per-product balance for a client. Staff can target any client;
   *  the service raises 403 when a non-staff caller passes someone
   *  else's UUID. */
  async getClientBalance(clientId: string): Promise<BalanceListResponse> {
    const res = await this.api.get<BalanceListResponse>(
      `/api/v1/portfolio/clients/${clientId}/balance`,
    );
    return res.data;
  }

  /** Shortcut: per-product balance for the authenticated client. */
  async getMyBalance(): Promise<BalanceListResponse> {
    const id = await authService.getMyId();
    return this.getClientBalance(id);
  }

  async getClientMovements(
    clientId: string,
    filters: MovementFilters = {},
  ): Promise<LedgerListResponse> {
    const res = await this.api.get<LedgerListResponse>(
      `/api/v1/portfolio/clients/${clientId}/movements`,
      { params: filters },
    );
    return res.data;
  }

  /** What corrections of a series' price per unit did for a client (S-52). Staff read the clients
   *  they serve; a client reads their own. */
  async listClientNavCorrections(clientId: string): Promise<NavCorrection[]> {
    const res = await this.api.get<{ items: NavCorrection[] }>(
      `/api/v1/portfolio/clients/${clientId}/nav-corrections`,
    );
    return res.data.items;
  }

  /** Pay now what a correction owes the client below the minimum payment, instead of waiting for
   *  their next payment (S-52). The client then chooses where it goes. Asking again changes nothing. */
  async requestNavCorrectionPayout(clientId: string, correctionId: string): Promise<OwedPayout> {
    const res = await this.api.post<OwedPayout>(
      `/api/v1/portfolio/clients/${clientId}/nav-corrections/${correctionId}/payout`,
    );
    return res.data;
  }

  /** Shortcut: the corrections of the authenticated client. */
  async listMyNavCorrections(): Promise<NavCorrection[]> {
    return this.listClientNavCorrections(await authService.getMyId());
  }

  /** Shortcut: movements for the authenticated client. */
  async getMyMovements(filters: MovementFilters = {}): Promise<LedgerListResponse> {
    const id = await authService.getMyId();
    return this.getClientMovements(id, filters);
  }

  /** Active investable products. Used by the deposit-propose form's
   *  Product dropdown and by the client's catalogue. Excludes the synthetic
   *  'Onboarding (system)' product. */
  async listProducts(): Promise<ProductSummary[]> {
    const res = await this.api.get<{ items: ProductSummary[] }>(
      '/api/v1/portfolio/products',
    );
    return res.data.items;
  }

  /** Every product, offered or not (managers and super admins): for the screens that
   *  prepare a product or report across products. */
  async listAllProducts(): Promise<ProductSummary[]> {
    const res = await this.api.get<{ items: ProductSummary[] }>('/api/v1/staff/products');
    return res.data.items;
  }

  async listClientStatements(
    clientId: string,
    filters: StatementFilters = {},
  ): Promise<Statement[]> {
    const res = await this.api.get<StatementListResponse>(
      `/api/v1/portfolio/clients/${clientId}/statements`,
      { params: filters },
    );
    return res.data.items;
  }

  /** Shortcut: statements for the authenticated client. */
  async listMyStatements(filters: StatementFilters = {}): Promise<Statement[]> {
    const id = await authService.getMyId();
    return this.listClientStatements(id, filters);
  }

  /** Short-lived presigned GET URL for one statement PDF. Owner-or-
   *  staff RBAC enforced on the server using the statement_id row. */
  async getStatementDownloadUrl(statementId: string): Promise<string> {
    const res = await this.api.get<{ download_url: string }>(
      `/api/v1/portfolio/statements/${statementId}/download`,
    );
    return res.data.download_url;
  }

  /** Client documents (0118) for one client, newest fact first; `kind`
   *  narrows to one document kind. Same RBAC as statements. */
  async listClientDocuments(
    clientId: string,
    kind?: ClientDocumentKind,
    productId?: string,
  ): Promise<ClientDocument[]> {
    const params: Record<string, string> = {};
    if (kind) params.kind = kind;
    if (productId) params.product_id = productId;
    const res = await this.api.get<ClientDocumentListResponse>(
      `/api/v1/portfolio/clients/${clientId}/documents`,
      { params },
    );
    return res.data.items;
  }

  /** Shortcut: documents for the authenticated client. */
  async listMyDocuments(kind?: ClientDocumentKind, productId?: string): Promise<ClientDocument[]> {
    const id = await authService.getMyId();
    return this.listClientDocuments(id, kind, productId);
  }

  /** Short-lived presigned GET URL for one document PDF. */
  async getDocumentDownloadUrl(documentId: string): Promise<string> {
    const res = await this.api.get<{ download_url: string }>(
      `/api/v1/portfolio/documents/${documentId}/download`,
    );
    return res.data.download_url;
  }

  /** Staff-only: (re)send the statement email to the client with a fresh
   *  7-day presigned download URL. */
  async sendStatementEmail(statementId: string): Promise<Statement> {
    const res = await this.api.post<Statement>(
      `/api/v1/portfolio/statements/${statementId}/send-email`,
    );
    return res.data;
  }

  /** Dashboard headline: total balance, accumulated yield, upcoming
   *  maturities (next 60 d), last 5 movements, commitments headline.
   *  Clients pass their own UUID; staff pass the target client's. */
  async getClientSummary(clientId: string): Promise<PortfolioSummary> {
    const res = await this.api.get<PortfolioSummary>(
      `/api/v1/portfolio/clients/${clientId}/summary`,
    );
    return res.data;
  }

  /** Shortcut: dashboard summary for the authenticated client. */
  async getMySummary(): Promise<PortfolioSummary> {
    const id = await authService.getMyId();
    return this.getClientSummary(id);
  }
}

export default new PortfolioService();
